import { Avatar, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useEffect, useState } from "react";
import { ManipulationTypes } from "../../enums/ManipulationTypes";
import { SUPERSET_COLORS, areSuperSetPartners } from "../../utils/exerciseUtils";
import { getExercise } from "../../services/ExcersiceService";
import { imageUrl as resizedImageUrl } from "../../utils/imageUrl";
import TraineeSwapNotice from "./TraineeSwapNotice";

const getManipulationLabel = (value) => {
    const type = ManipulationTypes.find(t => t.value === value);
    return type ? type.label : value;
};

const ProgramSummaryView = ({ exercises }) => {
    const [exerciseDefs, setExerciseDefs] = useState({});

    useEffect(() => {
        if (!exercises || exercises.length === 0) return;

        const idsToFetch = exercises
            .filter(ex => ex.exerciseId && !exerciseDefs[ex.exerciseId])
            .map(ex => ex.exerciseId);

        // Deduplicate
        const uniqueIds = [...new Set(idsToFetch)];

        uniqueIds.forEach(id => {
            getExercise(id).then(res => {
                setExerciseDefs(prev => ({ ...prev, [id]: res.data }));
            }).catch(() => {});
        });
    }, [exercises]);

    if (!exercises || exercises.length === 0) return null;

    const sorted = [...exercises].sort((a, b) => a.index > b.index ? 1 : -1);

    return (
        <TableContainer component={Paper} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: 'none' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={thStyle}>#</TableCell>
                        <TableCell sx={{ ...thStyle, textAlign: 'right' }}>תרגיל</TableCell>
                        <TableCell sx={thStyle}>סטים</TableCell>
                        <TableCell sx={thStyle}>חזרות</TableCell>
                        <TableCell sx={thStyle}>משקל</TableCell>
                        <TableCell sx={thStyle}>מניפולציה</TableCell>
                        <TableCell sx={thStyle}>בוצע</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((exercise, index) => {
                        const nextExercise = sorted[index + 1];
                        const prevExercise = index > 0 ? sorted[index - 1] : null;
                        const isFirstInSuperSet = areSuperSetPartners(exercise, nextExercise) && !areSuperSetPartners(exercise, prevExercise);
                        const isSecondInSuperSet = areSuperSetPartners(exercise, prevExercise);
                        const isSuperSet = isFirstInSuperSet || isSecondInSuperSet;

                        const def = exerciseDefs[exercise.exerciseId];
                        const name = def?.nameHeb || exercise.exerciseName || `תרגיל ${exercise.index + 1}`;
                        const imageUrl = def?.imageUrl || def?.coverUrl;
                        const sets = exercise.intervals?.length || 0;
                        const completedSets = exercise.intervals?.filter(i => i.reps > 0 || i.weight > 0).length || 0;
                        const bestWeight = exercise.intervals?.reduce((max, i) => Math.max(max, i.weight || 0), 0) || '-';
                        const bestReps = exercise.intervals?.reduce((max, i) => Math.max(max, i.reps || 0), 0) || '-';

                        return (
                            <TableRow key={exercise.id || exercise.uuid || index} sx={{
                                ...(isSuperSet && { backgroundColor: SUPERSET_COLORS.background }),
                            }}>
                                <TableCell sx={tdStyle}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                                        {isSuperSet && <SyncAltIcon sx={{ fontSize: 12, color: SUPERSET_COLORS.accent }} />}
                                        {exercise.index + 1}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ ...tdStyle, textAlign: 'right' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={resizedImageUrl(imageUrl, 24)} variant="rounded" sx={{ width: 24, height: 24, borderRadius: '4px', backgroundColor: '#F1F5F9' }}>
                                            <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 12 }} />
                                        </Avatar>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1E293B' }}>{name}</Typography>
                                        <TraineeSwapNotice
                                            replacedByTrainee={exercise.replacedByTrainee}
                                            originalExerciseNameHeb={exercise.originalExerciseNameHeb}
                                            originalExerciseName={exercise.originalExerciseName}
                                            variant="inline"
                                            dense
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={tdStyle}>{completedSets}/{sets}</TableCell>
                                <TableCell sx={tdStyle}>{bestReps}</TableCell>
                                <TableCell sx={tdStyle}>{bestWeight !== '-' ? `${bestWeight}kg` : '-'}</TableCell>
                                <TableCell sx={tdStyle}>{getManipulationLabel(exercise.manipulation)}</TableCell>
                                <TableCell sx={tdStyle}>
                                    {exercise.finished
                                        ? <CheckCircle sx={{ color: '#22C55E', fontSize: 18 }} />
                                        : <RadioButtonUnchecked sx={{ color: '#CBD5E1', fontSize: 18 }} />
                                    }
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const thStyle = {
    borderBottom: '1px solid #E2E8F0',
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '8px 6px',
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
};

const tdStyle = {
    borderBottom: '1px solid #F1F5F9',
    textAlign: 'center',
    fontSize: '0.8rem',
    padding: '6px',
    color: '#64748B',
};

export default ProgramSummaryView;
